import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import * as yup from 'yup';
import { Button, Typography } from 'antd';
import { FormikProvider, useFormik } from 'formik';
import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import { FieldInput, FieldCheckbox } from '../auth/login-formik-fields';
import { useCheckAgentUserExist } from '../auth/useCheckLandlordExist';
import { getSafeHtml } from 'common/helpers/safeHtml';

const { Title } = Typography;
const useStyles = makeStyles({
  formik: {
    width: '480px',
    '& > *:not(:first-child)': {
      width: '100%',
      marginTop: 16,
    },
  },
  title: {
    lineHeight: '38px !important',
    margin: '0 !important',
    opacity: 0.85,
  },
});

const SignupOrLogin = ({ handleNextContent, setEmail }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const onCompleted = e => {
    handleNextContent(e);
  };

  const [queryCheckAgentUser, loading] = useCheckAgentUserExist({
    onCompleted,
  });

  const initialValues = useMemo(() => ({ email: '', termsAccepted: false }), []);

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        email: yup
          .string()
          .label('Email')
          .email(t('landlord.auth.login.format_error.username'))
          .max(200)
          .required(t('this_part_cannot_be_empty')),
        termsAccepted: yup.bool().oneOf([true], 'You must accept terms first.'),
      }),
    [t],
  );

  const handleSubmit = async values => {
    setEmail(values.email);
    await queryCheckAgentUser({
      variables: {
        type: 'EMAIL',
        value: values?.email,
      },
    });
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
    validateOnBlur: false,
  });

  const handleCheckboxChange = ev => {
    formik.setFieldValue('termsAccepted', ev.target.checked);
  };

  return (
    <div className={classes.formik}>
      <Title level={2} className={classes.title}>
        {t('log_in_or_sign_up')}
      </Title>
      <FormikProvider value={formik}>
        <FieldInput
          fieldConfig="email"
          placeholder={t('email_address')}
          style={{ marginTop: 40 }}
        />
        <Button type="primary" size="large" loading={loading} onClick={formik.submitForm}>
          {t('continue')}
        </Button>
        <FieldCheckbox
          fieldConfig="termsAccepted"
          onChange={handleCheckboxChange}
          label={
            <span>
              {getSafeHtml(
                t('agent.auth.terms.summary').replaceAll(/\$(.*?)\$/g, match => {
                  const Host =
                    window.location.host === 'spa.dandythrust.com'
                      ? 'https://hurricane-www.dandythrust.com'
                      : 'https://www.student.com';
                  switch (match) {
                    case '$terms$':
                      return `<a target="_blank" href="${Host}/terms/terms-of-use-marketplace">${t(
                        'agent.auth.terms.terms',
                      )}</a>`;
                    case '$private$':
                      return `<a target="_blank" href="${Host}/terms/privacy-marketplace">${t(
                        'agent.auth.terms.private_policy',
                      )}</a>`;
                    case '$cookies$':
                      return `<a target="_blank" href="${Host}/terms/cookies">${t(
                        'agent.auth.terms.cookies_policy',
                      )}</a>`;
                    default:
                      return '';
                  }
                }),
              )}
            </span>
          }
        />
        <Typography>
          {t('you_will_be_contacted_by_our_staff_to_agree_to_our_terms_and_conditions')}
        </Typography>
      </FormikProvider>
    </div>
  );
};

SignupOrLogin.propTypes = {
  handleNextContent: PropTypes.func.isRequired,
  setEmail: PropTypes.func.isRequired,
};

export default SignupOrLogin;
